import { format } from "date-fns";

/** Parse a canonical entry date (`YYYY-MM-DD`) or form date (`MM/dd/yyyy`). */
export function parseEntryDate(dateStr: string): Date {
	const iso = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (iso) {
		const [, yyyy, mm, dd] = iso;
		return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
	}
	const [mm, dd, yyyy] = dateStr.split("/").map(Number);
	return new Date(yyyy, mm - 1, dd);
}

export interface DateRange {
	from: Date;
	to: Date;
}

export interface DateRangeBoundary {
	from?: Date;
	to?: Date;
}

/**
 * Date to prefill when adding from an entries view. Favor the range end, but
 * never default an entry to a future calendar date.
 */
export function getAddEntryDefaultDate(
	selectedDate: Date,
	range: DateRangeBoundary,
): Date {
	const candidate =
		range.from && range.to
			? range.from > range.to
				? range.from
				: range.to
			: (range.to ?? range.from ?? selectedDate);
	const today = startOfLocalDay(new Date());
	return localDateKey(candidate) > localDateKey(today) ? today : candidate;
}

/** Format a Date for v1/v2 entry payloads that still expect "MM/dd/yyyy". */
export function formatDateForApi(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const year = date.getFullYear();
	return `${month}/${day}/${year}`;
}

/** Convert a MM/dd/yyyy form date into the canonical YYYY-MM-DD entry wire format. */
export function formDateToEntryDate(d: string): string {
	const [mm, dd, yyyy] = d.split("/").map(Number);
	if (!mm || !dd || !yyyy) return d;
	return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

/** Stable local calendar key suitable for memo/selection scopes. */
export function localDateKey(date: Date | undefined): string {
	if (!date) return "";
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${date.getFullYear()}-${month}-${day}`;
}

export function isSameLocalDate(left: Date, right: Date): boolean {
	return (
		left.getFullYear() === right.getFullYear() &&
		left.getMonth() === right.getMonth() &&
		left.getDate() === right.getDate()
	);
}

export function isCompleteDateRange(
	range: DateRangeBoundary | null | undefined,
): range is DateRange {
	return !!(range?.from && range.to);
}

export function formatDateRange(range: DateRange): string {
	if (isSameLocalDate(range.from, range.to)) return format(range.from, "MMM d");
	const currentYear = new Date().getFullYear();
	const fromYear = range.from.getFullYear();
	const toYear = range.to.getFullYear();
	if (fromYear !== toYear) {
		return `${format(range.from, "MMM d, yyyy")} – ${format(range.to, "MMM d, yyyy")}`;
	}
	if (fromYear !== currentYear) {
		return `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}`;
	}
	return `${format(range.from, "MMM d")} – ${format(range.to, "MMM d")}`;
}

export function startOfLocalDay(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function endOfLocalDay(date: Date): Date {
	return new Date(
		date.getFullYear(),
		date.getMonth(),
		date.getDate(),
		23,
		59,
		59,
		999,
	);
}

export function isWithinLocalDateRange(
	date: Date,
	range: DateRangeBoundary,
): boolean {
	if (!range.from || !range.to) return false;
	return date >= startOfLocalDay(range.from) && date <= endOfLocalDay(range.to);
}
